import * as React from 'react'

import { MastodonPost } from './components/Post'
import { Empty } from '../Common/Empty'
import { getPostPreviewEntries } from '../Common/getPostPreviewEntries'
import { PostPreviewCard } from '../Common/styles'
import type { MastodonProfile, MastodonDraft, MastodonType } from '../types'

import styles from './MastodonPreview.module.css'

import type { JSX } from 'react'

export const MastodonPreview = ({
  draft,
  profile,
}: {
  draft: MastodonDraft
  profile: MastodonProfile
}): JSX.Element => {
  const { username, serverUrl } = profile
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const singlePostIdentityRef = React.useRef<object>({})
  const { hasThread, isEmpty, service } = draft

  const getPosts = (): MastodonType[] => {
    const drafts: MastodonType[] = []

    if (hasThread) {
      const { activeThreadId } = draft

      draft.thread?.forEach((threadedDraft, i) => {
        drafts.push({
          content:
            activeThreadId === i
              ? {
                  spoilerText: draft.channelData?.mastodon?.spoilerText,
                  text: draft.text,
                  enabledAttachmentType: draft.enabledAttachmentType,
                  images: draft.images,
                  video: draft.video,
                  gif: draft.gif,
                  link: draft.link,
                }
              : {
                  ...threadedDraft,
                  spoilerText: draft.channelData?.mastodon?.spoilerText,
                },
        })
      })
    } else {
      drafts.push({
        content: {
          ...draft,
          spoilerText: draft.channelData?.mastodon?.spoilerText,
          text: draft.text,
        },
      })
    }

    return drafts
  }

  const posts = getPosts()
  const postEntries = getPostPreviewEntries(
    posts,
    (post) => post.content,
    (_post, index) => draft.thread?.[index] ?? singlePostIdentityRef.current,
  )

  React.useEffect(() => {
    const container = containerRef.current
    // Disabled auto-scroll when in omni mode
    if (!container || service.isOmni) return
    container.scrollTop = container.scrollHeight
  }, [posts, service.isOmni])

  if (isEmpty && !hasThread) {
    return <Empty />
  }

  return (
    <PostPreviewCard
      className={styles.mastodonPreview}
      data-testid="mastodon-preview"
      ref={containerRef}
    >
      {postEntries.map(({ key, post }, index) => (
        <MastodonPost
          key={key}
          isReply={hasThread && index > 0}
          content={post.content}
          profile={{
            ...profile,
            handle: `@${username}`,
            serverUrl,
          }}
        />
      ))}
    </PostPreviewCard>
  )
}
